import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import { Button, Input, Label, Modal } from './ui';
import { cn } from '../lib/cn';
import { CLASS_PALETTE } from '../utils/classColors';
import { classesApi } from '../services/annotatorApi';
import { APIClientError } from '../services/apiClient';
import type { Class as ClassModel } from '../types/api';

/**
 * EditClassModal — same structure as AddClassModal but in PATCH mode for an
 * existing class. Reuses the modal chrome, name field, swatch grid, custom
 * hex expander, and structured error handling. The only differences are:
 *   - Pre-filled name + color from the passed-in class
 *   - "Save" / "Update" CTA instead of "Create"
 *   - Calls `classesApi.update` instead of `classesApi.create`
 *   - Only PATCHes fields that actually changed (skips no-op submits)
 */
interface EditClassModalProps {
    open: boolean;
    onClose: () => void;
    orgId?: string;
    projectId: string;
    cls: ClassModel | null;
    existingClasses: ReadonlyArray<ClassModel>;
    isDarkMode?: boolean;
    /** Called after the class is successfully updated. */
    onUpdated?: (cls: ClassModel) => void;
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export default function EditClassModal({
    open,
    onClose,
    orgId,
    projectId,
    cls,
    existingClasses,
    isDarkMode = false,
    onUpdated,
}: EditClassModalProps) {
    const { t } = useTranslation('modals');
    const { t: tCommon } = useTranslation('common');
    const { t: tErrors } = useTranslation('errors');

    const [name, setName] = useState('');
    const [color, setColor] = useState<string>('');
    const [customMode, setCustomMode] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [nameError, setNameError] = useState<string | null>(null);
    const [colorError, setColorError] = useState<string | null>(null);

    // Prefill on open / when the target class changes.
    useEffect(() => {
        if (!open || !cls) return;
        setName(cls.name);
        setColor(cls.color ?? '');
        setCustomMode(!!cls.color && !(CLASS_PALETTE as readonly string[]).includes(cls.color));
        setNameError(null);
        setColorError(null);
        setSubmitting(false);
    }, [open, cls]);

    if (!cls) return null;

    function validate(): boolean {
        if (!cls) return false;
        const trimmed = name.trim();
        let ok = true;
        if (!trimmed) {
            setNameError(tCommon('required_field'));
            ok = false;
        } else if (trimmed.length > 60) {
            setNameError(t('class_name_too_long', { defaultValue: 'Name too long (max 60 chars).' }));
            ok = false;
        } else if (
            trimmed.toLowerCase() !== cls.name.toLowerCase() &&
            existingClasses.some((c) => c.class_index !== cls.class_index && c.name.toLowerCase() === trimmed.toLowerCase())
        ) {
            setNameError(tErrors('class_name_taken'));
            ok = false;
        } else {
            setNameError(null);
        }
        if (color && !HEX_RE.test(color)) {
            setColorError(t('class_color_invalid', { defaultValue: 'Color must be #RRGGBB hex.' }));
            ok = false;
        } else {
            setColorError(null);
        }
        return ok;
    }

    async function submit() {
        if (!cls) return;
        if (!validate()) return;
        // Build PATCH body with only the fields that actually changed.
        const trimmed = name.trim();
        const patch: { name?: string; color?: string } = {};
        if (trimmed !== cls.name) patch.name = trimmed;
        if ((color || '') !== (cls.color ?? '')) patch.color = color;
        if (Object.keys(patch).length === 0) {
            onClose();
            return;
        }
        setSubmitting(true);
        try {
            const updated = await classesApi.update(projectId, cls.class_index, patch, orgId);
            toast.success(t('class_updated', { defaultValue: 'Class updated' }));
            onUpdated?.(updated);
            onClose();
        } catch (err) {
            if (err instanceof APIClientError) {
                if (err.code === 'class_name_taken') {
                    setNameError(tErrors('class_name_taken'));
                } else if (err.code === 'invalid_input') {
                    const fieldErr = err.body.fields?.find((f) => f.field === 'name');
                    if (fieldErr) setNameError(fieldErr.issue);
                    const colorFieldErr = err.body.fields?.find((f) => f.field === 'color');
                    if (colorFieldErr) setColorError(colorFieldErr.issue);
                    if (!fieldErr && !colorFieldErr) toast.error(tErrors('invalid_input'));
                } else {
                    toast.error(err.body.message ?? err.code);
                }
            } else {
                toast.error(tErrors('generic'));
            }
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Modal
            open={open}
            onClose={submitting ? () => {} : onClose}
            title={t('edit_class_title', { defaultValue: 'Edit class' })}
            widthClass="max-w-md"
            isDarkMode={isDarkMode}
            footer={
                <>
                    <Button variant="ghost" isDarkMode={isDarkMode} onClick={onClose} disabled={submitting}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="success" onClick={() => void submit()} disabled={submitting}>
                        {tCommon('save', { defaultValue: 'Save' })}
                    </Button>
                </>
            }
        >
            <div className="space-y-5">
                {/* Name */}
                <div>
                    <Label isDarkMode={isDarkMode} required>
                        {t('class_name_label')}
                    </Label>
                    <Input
                        isDarkMode={isDarkMode}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        error={!!nameError}
                        maxLength={60}
                        placeholder={t('class_name_placeholder', { defaultValue: 'e.g. apple, pedestrian, defect' })}
                    />
                    {nameError && <p className="text-xs text-red-500 mt-1.5">{nameError}</p>}
                </div>

                {/* Color */}
                <div>
                    <Label isDarkMode={isDarkMode}>{t('class_color_label')}</Label>

                    <div className="grid grid-cols-6 gap-2 mt-1">
                        {CLASS_PALETTE.map((c) => {
                            const selected = c === color;
                            return (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => { setColor(c); setCustomMode(false); }}
                                    className={cn(
                                        'h-9 w-full rounded-md border-2 transition-all duration-100',
                                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
                                        selected
                                            ? isDarkMode ? 'border-white scale-110' : 'border-gray-900 scale-110'
                                            : isDarkMode ? 'border-gray-700' : 'border-gray-200',
                                    )}
                                    style={{ backgroundColor: c }}
                                    aria-label={`Color ${c}`}
                                    aria-pressed={selected}
                                />
                            );
                        })}
                    </div>

                    <button
                        type="button"
                        onClick={() => setCustomMode((v) => !v)}
                        className={cn(
                            'text-[12px] mt-2 underline-offset-2 hover:underline',
                            isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700',
                        )}
                    >
                        {customMode
                            ? t('class_color_hide_custom', { defaultValue: 'Hide custom color' })
                            : t('class_color_or_custom')}
                    </button>

                    {customMode && (
                        <div className="flex items-center gap-2 mt-2">
                            <span
                                className="h-9 w-9 rounded-md border shrink-0"
                                style={{
                                    backgroundColor: HEX_RE.test(color) ? color : 'transparent',
                                    borderColor: isDarkMode ? '#475569' : '#d1d5db',
                                }}
                                aria-hidden="true"
                            />
                            <Input
                                isDarkMode={isDarkMode}
                                value={color}
                                onChange={(e) => setColor(e.target.value.toLowerCase())}
                                placeholder="#3b82f6"
                                maxLength={7}
                                error={!!colorError}
                                className="font-mono uppercase"
                            />
                        </div>
                    )}
                    {colorError && <p className="text-xs text-red-500 mt-1.5">{colorError}</p>}
                </div>
            </div>
        </Modal>
    );
}
